import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Zap, 
  Building2,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  LogOut,
  FileText,
  Euro,
  Calendar,
  AlertCircle,
  Trash2,
  ArrowDownLeft,
  ArrowUpRight,
  ShieldCheck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SepaPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mandate, setMandate] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [bills, setBills] = useState([]);
  
  const [formData, setFormData] = useState({
    iban: '',
    account_holder: '',
    bic: ''
  });
  const [ibanValid, setIbanValid] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [mandateRes, txRes, billsRes] = await Promise.all([
        axios.get(`${API_URL}/api/sepa/mandate`, { headers }),
        axios.get(`${API_URL}/api/sepa/transactions`, { headers }),
        axios.get(`${API_URL}/api/billing/monthly`, { headers })
      ]);
      
      setMandate(mandateRes.data.mandate);
      setTransactions(txRes.data.transactions || []);
      setBills(billsRes.data.bills || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateIban = async (iban) => {
    if (iban.length < 15) {
      setIbanValid(null);
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/sepa/validate-iban?iban=${encodeURIComponent(iban)}`
      );
      setIbanValid(response.data);
    } catch (error) {
      setIbanValid({ valid: false, error: 'IBAN invalide' });
    }
  };

  const handleIbanChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
    setFormData({ ...formData, iban: formatted });
    validateIban(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!ibanValid?.valid) {
      toast.error('Veuillez entrer un IBAN valide');
      return;
    }
    
    if (!formData.account_holder) {
      toast.error('Veuillez entrer le nom du titulaire');
      return;
    }
    
    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${API_URL}/api/sepa/mandate`,
        {
          iban: formData.iban.replace(/\s/g, ''),
          account_holder: formData.account_holder,
          bic: formData.bic || null
        },
        { headers }
      );
      
      toast.success('Mandat SEPA configuré avec succès !');
      setMandate(response.data);
      setFormData({ iban: '', account_holder: '', bic: '' });
      setIbanValid(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelMandate = async () => {
    if (!confirm('Êtes-vous sûr de vouloir annuler votre mandat SEPA ?')) {
      return;
    }
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/api/sepa/mandate`, { headers });
      toast.success('Mandat SEPA annulé');
      setMandate(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const handleGenerateBill = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/billing/generate`, {}, { headers });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la génération');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const isProducer = user?.user_type === 'producer';

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/wallet" className="nav-link">Mon Compte</Link>
              <Link to="/sepa" className="nav-link active">SEPA</Link>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  Prélèvement SEPA
                </h1>
                <p className="text-muted-foreground">
                  {isProducer 
                    ? 'Recevez vos revenus automatiquement chaque mois'
                    : 'Payez vos factures d\'énergie automatiquement'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Mandate Status */}
          {mandate ? (
            <Card className="mb-6 border-green-200 bg-green-50/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <ShieldCheck className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Mandat SEPA actif</h3>
                      <p className="text-sm text-muted-foreground">
                        Compte : {mandate.iban_masked}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Référence : {mandate.mandate_reference}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {mandate.status === 'active' ? 'Actif' : 'En attente'}
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCancelMandate}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Annuler
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Setup Form */
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Configurer le prélèvement automatique
                </CardTitle>
                <CardDescription>
                  {isProducer 
                    ? 'Entrez votre IBAN pour recevoir vos revenus automatiquement'
                    : 'Entrez votre IBAN pour payer vos factures automatiquement'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="iban">IBAN *</Label>
                    <div className="relative">
                      <Input
                        id="iban"
                        value={formData.iban}
                        onChange={handleIbanChange}
                        placeholder="FR76 1234 5678 9012 3456 7890 123"
                        className={`font-mono ${
                          ibanValid?.valid === true 
                            ? 'border-green-500 focus:ring-green-500' 
                            : ibanValid?.valid === false 
                              ? 'border-red-500 focus:ring-red-500'
                              : ''
                        }`}
                      />
                      {ibanValid?.valid === true && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                      )}
                      {ibanValid?.valid === false && (
                        <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                      )}
                    </div>
                    {ibanValid?.valid && (
                      <p className="text-xs text-green-600">
                        IBAN valide - Pays : {ibanValid.country}
                      </p>
                    )}
                    {ibanValid?.valid === false && (
                      <p className="text-xs text-red-600">{ibanValid.error}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="account_holder">Titulaire du compte *</Label>
                    <Input
                      id="account_holder"
                      value={formData.account_holder}
                      onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                      placeholder="Prénom NOM"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bic">BIC (optionnel)</Label>
                    <Input
                      id="bic"
                      value={formData.bic}
                      onChange={(e) => setFormData({ ...formData, bic: e.target.value.toUpperCase() })}
                      placeholder="BNPAFRPP"
                      className="font-mono"
                    />
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-2">En signant ce mandat :</p>
                    <ul className="space-y-1 list-disc list-inside">
                      {isProducer ? (
                        <>
                          <li>Vous recevrez automatiquement vos revenus chaque mois</li>
                          <li>Les virements sont effectués sous 3 jours ouvrés</li>
                        </>
                      ) : (
                        <>
                          <li>Vous autorisez Green Teva à prélever vos factures mensuelles</li>
                          <li>Les prélèvements sont effectués le 5 de chaque mois</li>
                        </>
                      )}
                      <li>Vous pouvez annuler ce mandat à tout moment</li>
                    </ul>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={submitting || !ibanValid?.valid}
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Signer le mandat SEPA
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Bills */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Factures mensuelles
                </CardTitle>
                <Button variant="outline" size="sm" onClick={handleGenerateBill}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Générer
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {bills.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune facture</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bills.map((bill, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          bill.status === 'paid' ? 'bg-green-100' : 'bg-orange-100'
                        }`}>
                          {bill.status === 'paid' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-orange-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">
                            {new Date(bill.period_start).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {bill.total_energy_kwh} kWh
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">
                          {isProducer ? '+' : ''}{bill.net_amount?.toFixed(2)}€
                        </p>
                        {isProducer && bill.commission_amount > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Commission: {bill.commission_amount?.toFixed(2)}€
                          </p>
                        )}
                        <Badge className={
                          bill.status === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-orange-100 text-orange-700'
                        }>
                          {bill.status === 'paid' ? 'Payé' : 'En attente'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* SEPA Transactions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Euro className="w-5 h-5" />
                {isProducer ? 'Virements reçus' : 'Prélèvements effectués'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8">
                  <Euro className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune transaction SEPA</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.status === 'completed' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {isProducer ? (
                            <ArrowDownLeft className="w-5 h-5 text-green-600" />
                          ) : (
                            <ArrowUpRight className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{tx.description}</p>
                          <p className="text-sm text-muted-foreground">
                            Réf: {tx.reference}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${isProducer ? 'text-green-600' : ''}`}>
                          {isProducer ? '+' : '-'}{tx.amount?.toFixed(2)}€
                        </p>
                        <Badge className={
                          tx.status === 'completed' 
                            ? 'bg-green-100 text-green-700' 
                            : tx.status === 'scheduled'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-orange-100 text-orange-700'
                        }>
                          {tx.status === 'completed' ? 'Effectué' : 
                           tx.status === 'scheduled' ? 'Programmé' : 'En cours'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SepaPage;
